// Microsoft sign-in and signup through the Office Dialog API. This is the ONLY file besides
// `authdialog/index.ts` allowed to touch `Office.context.ui` (documented exception to the
// "Office.js only in ExcelToolExecutor" rule — it never touches the workbook).
//
// Flow: open `/auth-dialog.html?flow=<microsoft|signup>` (same origin, required by Office) → the page
// forwards to `${API}/login/microsoft?addin_origin=<origin>` (→ Microsoft → the API) or to the account
// portal's `/signup?client=addin&addin_origin=<origin>` → either redirects to
// `/auth-dialog.html#access_token=…` → the page calls `messageParent` → we resolve and close.

export interface SsoTokens {
  access_token: string;
  token_type: string;
  refresh_token: string | null;
}

export type SsoErrorCode = 'cancelled' | 'unsupported' | 'timeout' | 'signup_disabled' | 'sso_failed' | 'account_disabled' | 'origin_not_allowed' | 'dialog_error' | 'bad_message'
  // a first sign-in refused to open an account on this address (sculpt-api accounts/emails.py)
  | 'email_plus_address' | 'email_disposable_domain';

export class SsoError extends Error {
  constructor(
    public code: SsoErrorCode,
    detail?: string,
  ) {
    super(detail ? `${code}: ${detail}` : code);
  }
}

export const SSO_TIMEOUT_MS = 5 * 60 * 1000;
/** Office error code raised when the user closes the dialog window. */
const DIALOG_CLOSED_BY_USER = 12006;

export type AuthDialogFlow = 'microsoft' | 'signup';

export function dialogUrl(origin: string = window.location.origin, flow: AuthDialogFlow = 'microsoft'): string {
  return `${origin}/auth-dialog.html?flow=${flow}`;
}

/** Dialog size in percent of the Excel window: the signup form needs more room than a Microsoft prompt. */
const DIALOG_SIZE: Record<AuthDialogFlow, { height: number; width: number }> = { microsoft: { height: 60, width: 30 }, signup: { height: 75, width: 36 } };

type DialogMessage = { message?: string; error?: number };

/** Opens the Microsoft sign-in dialog and resolves with the tokens posted back by the dialog page. */
export function signInWithMicrosoft(opts: { timeoutMs?: number; url?: string } = {}): Promise<SsoTokens> {
  return openAuthDialog('microsoft', opts);
}

/** Opens the account portal's signup inside the dialog (sculpt-account's add-in handoff) and resolves with the
 *  tokens it posts back: the user is signed in when the dialog closes, no second login in the pane. */
export function signUpInDialog(opts: { timeoutMs?: number; url?: string } = {}): Promise<SsoTokens> {
  return openAuthDialog('signup', opts);
}

function openAuthDialog(flow: AuthDialogFlow, opts: { timeoutMs?: number; url?: string }): Promise<SsoTokens> {
  const ui = typeof Office !== 'undefined' ? Office.context?.ui : undefined;
  if (!ui || typeof ui.displayDialogAsync !== 'function') {
    return Promise.reject(new SsoError('unsupported'));
  }
  const url = opts.url ?? dialogUrl(undefined, flow);
  const timeoutMs = opts.timeoutMs ?? SSO_TIMEOUT_MS;
  const size = DIALOG_SIZE[flow];

  return new Promise<SsoTokens>((resolve, reject) => {
    let dialog: Office.Dialog | null = null;
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        dialog?.close();
      } catch {
        /* already closed */
      }
      fn();
    };
    const timer = setTimeout(() => finish(() => reject(new SsoError('timeout'))), timeoutMs);

    ui.displayDialogAsync(url, { ...size, promptBeforeOpen: false }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        finish(() => reject(new SsoError('dialog_error', String(result.error?.code ?? result.error?.message ?? 'open failed'))));
        return;
      }
      dialog = result.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogMessage) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(arg.message ?? '');
        } catch {
          finish(() => reject(new SsoError('bad_message')));
          return;
        }
        const msg = parsed as { type?: string; error?: string; access_token?: string; token_type?: string; refresh_token?: string | null };
        if (msg.type === 'tokens' && typeof msg.access_token === 'string') {
          finish(() => resolve({ access_token: msg.access_token as string, token_type: msg.token_type || 'bearer', refresh_token: msg.refresh_token ?? null }));
        } else if (msg.type === 'error') {
          // The API lands `#error=cancelled` when the user backs out at Microsoft: same as closing the dialog.
          finish(() => reject(new SsoError(msg.error === 'cancelled' ? 'cancelled' : asSsoErrorCode(msg.error), msg.error)));
        } else {
          finish(() => reject(new SsoError('bad_message')));
        }
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogMessage) => {
        if (arg.error === DIALOG_CLOSED_BY_USER) finish(() => reject(new SsoError('cancelled')));
        else finish(() => reject(new SsoError('dialog_error', String(arg.error))));
      });
    });
  });
}

function asSsoErrorCode(code: string | undefined): SsoErrorCode {
  switch (code) {
    case 'signup_disabled':
    case 'sso_failed':
    case 'account_disabled':
    case 'origin_not_allowed':
    case 'email_plus_address':
    case 'email_disposable_domain':
      return code;
    default:
      return 'sso_failed';
  }
}
